import { useState, useCallback } from 'react';
import { defineMessages, FormattedMessage } from '@alltrails/shared/react-intl';
import { useIntl } from '@alltrails/shared/react-intl';
import ModalInterior from '@alltrails/shared/components/modals/ModalInterior';
import ModalBackground from '@alltrails/shared/components/modals/ModalBackground';
import useActivityStrings from '@alltrails/shared/hooks/useActivityStrings';
import useMultiStepModal from '@alltrails/shared/hooks/useMultiStepModal';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import { usersContentPrivacyPolicy } from 'utils/UserUtils';
import DropzonePad from '../DropzonePad';
import IndividualPrivacyModal from '../../privacyPolicy/modals/IndividualPrivacyModal';
import TrackFileUpload from '../TrackFileUpload';
import TrackUploadFields from '../TrackUploadFields';
import TrailFormSuccess from '../../shared/TrailFormSuccess';
import useDropzoneUpload from '../../../hooks/useDropzoneUpload';
import useScreenLock from '../../../hooks/useScreenLock';
import { updateTrack, deleteTrack } from '../../../utils/requests/track_upload_requests';
import { generateUploadModalCta } from '../../../utils/upload_helpers';
import { policyToRadioForm, VISIBILITY_PUBLIC, ACTIVITIES } from '../../../utils/privacy_policy_helpers';
import { logIndividualContentPrivacyChangedAmplitudeEvent } from '../../../utils/privacy_policy_amplitude_helpers';
import useUser from 'hooks/useUser';
import * as styles from './styles/styles.module.scss';
import * as formStyles from '../styles/styles.module.scss';

const ACTIVITY_UPLOAD_STRINGS = defineMessages({
  ERROR_SAVING: { defaultMessage: 'Error saving, please try again.' },
  NEXT: { defaultMessage: 'Next' },
  BACK: { defaultMessage: 'Back' },
  UPDATE: { defaultMessage: 'Update' },
  UPLOAD_ACTIVITY: { defaultMessage: 'Upload activity' },
  EDIT_ACTIVIY: { defaultMessage: 'Edit activity' }
});

const ActivityUploadModal = ({
  url,
  closeModal,
  showSmoothOption,
  trailId,
  createAsMap,
  systemOwned,
  onTrackAdd,
  routeMode,
  handleRouteUpload,
  trackParams,
  disableDropzone,
  initialMap
}) => {
  useScreenLock();
  const { findByValue } = useActivityStrings();
  const {
    formattedDefaultMessages: { ERROR_SAVING, NEXT, BACK, UPDATE, UPLOAD_ACTIVITY, EDIT_ACTIVIY }
  } = useFormatMessage(ACTIVITY_UPLOAD_STRINGS);
  const user = useUser();
  const editMode = trackParams !== null;
  const [activeFile, setActiveFile] = useState(null);
  const [smoothFile, toggleSmoothFile] = useState(true);
  const [uploading, toggleUploading] = useState(false);
  const [progress, updateProgress] = useState(0);
  const [map, updateMap] = useState(initialMap);
  const [errorMessage, updateErrorMessage] = useState(null);
  const { mountedModal, linkedState, goBack, navigateTo, handleCloseModal, handleOnTransitionEnd } = useMultiStepModal(
    editMode ? 'activityForm' : 'dropzonePad',
    closeModal
  );
  const defaultContentPrivacy = usersContentPrivacyPolicy(user, ACTIVITIES);
  const [trackFormParams, updateTrackFormParams] = useState(
    trackParams || {
      name: '',
      description: '',
      rating: 0,
      trailId,
      activity: findByValue(),
      date: new Date(), // this date will change once a file is uploaded
      isPrivate: false,
      contentPrivacy: defaultContentPrivacy || VISIBILITY_PUBLIC
    }
  );
  const intl = useIntl();

  const updateTrackForm = payload => {
    updateTrackFormParams(prevState => {
      return { ...prevState, ...payload };
    });
  };
  /* Dropzone callback start */
  const addedFileCallback = useCallback(file => {
    setActiveFile(file);
    navigateTo('uploadCenter');
  }, []);

  const handleErrorCallback = useCallback((file, errorMessage) => {
    updateErrorMessage(errorMessage || ERROR_SAVING);
    updateProgress(0);
    toggleUploading(false);
  }, []);

  const sendingCallback = useCallback(
    (file, formData = new FormData()) => {
      formData.append('presentation_type', 'track');
      formData.append('smooth', smoothFile ? 'on' : 'off');

      if (trailId) {
        formData.append('trail_id', trackFormParams.trailId);
      }

      if (createAsMap) {
        formData.append('create_as_map', createAsMap);
      }

      if (systemOwned) {
        formData.append('system_owned', true);
      }
    },
    [createAsMap, trackFormParams.trailId, smoothFile]
  );

  const removedFileCallback = useCallback(file => {
    navigateTo('dropzonePad');
    setActiveFile(null);
  }, []);

  const progressCallback = useCallback(fileProgress => {
    const progressInt = fileProgress === 100 ? 99 : parseInt(fileProgress.toFixed(0));

    updateProgress(progressInt);
  }, []);

  const handleRouteModeUpload = useCallback(
    uploadedMap => {
      const payload = createAsMap ? uploadedMap : uploadedMap.routes[uploadedMap.routes.length - 1];
      handleRouteUpload(payload);
      navigateTo('success');
    },
    [createAsMap]
  );

  const successCallback = useCallback(
    (file, response) => {
      const uploadedMap = response.maps[0];
      updateProgress(100);
      updateMap(uploadedMap);
      toggleUploading(false);
      if (routeMode) {
        handleRouteModeUpload(uploadedMap);
        return;
      }

      updateTrackForm({ date: new Date(uploadedMap.created_at) });
      navigateTo('activityForm');
    },
    [routeMode]
  );
  /* Dropzone callback End */

  const [dropzone, initDropzone] = useDropzoneUpload({
    url,
    formParam: '#uploadForm',
    paramName: 'file',
    maxFiles: 1,
    maxFilesize: 256,
    acceptedFiles: '.gpx,.tpo,.fit,.tcx,.kml,.kmz',
    addedFileCallback,
    handleErrorCallback,
    sendingCallback,
    removedFileCallback,
    progressCallback,
    successCallback,
    disabled: disableDropzone
  });

  const handleDeleteSuccess = () => {
    updateMap(null);
    removedFileCallback();
  };

  const deleteRecording = () => {
    deleteTrack(map.id)
      .then(handleDeleteSuccess)
      .catch(err => console.error('ActivityUploadModal delete', err));
  };

  const removeFileAndGoBack = () => {
    deleteRecording();
    goBack();
  };

  const setGoBack = () => {
    if (linkedState.key === 'success') {
      return null;
    }

    if (linkedState.key === 'uploadCenter' && map) {
      return removeFileAndGoBack;
    }

    return goBack;
  };

  const uploadRecording = () => {
    updateErrorMessage(null);
    toggleUploading(true);
    dropzone.processQueue();
  };

  const removeFile = file => {
    dropzone.removeFile(file);

    if (map && map.id) {
      deleteRecording();
      updateProgress(0);
    }
  };

  const openDropzone = () => dropzone.hiddenFileInput.click();

  const updateErrorCallback = () => {
    toggleUploading(false);
  };

  const updateSuccessCallback = maps => {
    const payload = { detail: maps[0] };
    toggleUploading(false);

    onTrackAdd(payload);
    if (editMode) {
      handleCloseModal();
    } else {
      navigateTo('success');
    }
  };

  const updateRecording = () => {
    logIndividualContentPrivacyChangedAmplitudeEvent(
      ACTIVITIES,
      map?.id,
      defaultContentPrivacy,
      trackParams?.contentPrivacy,
      trackFormParams?.contentPrivacy
    );

    toggleUploading(true);
    updateTrack(map.slug, trackFormParams.trailId, trackFormParams).then(updateSuccessCallback).catch(updateErrorCallback);
  };

  const handlePrivacyTriggerClick = () => {
    navigateTo('privacyPolicy');
  };

  const baseCtas = () => {
    switch (linkedState.key) {
      case 'dropzonePad': {
        return {
          primaryCta: generateUploadModalCta(() => {}, NEXT, true)
        };
      }
      case 'uploadCenter':
        return {
          primaryCta: generateUploadModalCta(uploadRecording, NEXT, uploading),
          secondaryCta: generateUploadModalCta(() => removeFile(activeFile), BACK, uploading)
        };
      case 'activityForm':
        return {
          primaryCta: generateUploadModalCta(updateRecording, editMode ? UPDATE : UPLOAD_ACTIVITY, uploading),
          secondaryCta: generateUploadModalCta(editMode ? handleCloseModal : goBack, BACK, uploading)
        };
      default:
        return null;
    }
  };

  const dropzonePad = (
    <ModalInterior
      title={UPLOAD_ACTIVITY}
      description={<FormattedMessage defaultMessage="Step {currentStep} of {finalStep}" values={{ currentStep: 1, finalStep: 2 }} />}
      closeModal={handleCloseModal}
      goBack={setGoBack()}
      isMounted={linkedState.key === mountedModal}
      onTransitionEnd={handleOnTransitionEnd}
      baseCtas={baseCtas()}
    >
      <div className={styles.dropContainer}>
        <span className={styles.uploadForm} data-testid="uploadForm">
          <DropzonePad variant="tracks" openDropzone={openDropzone} />
        </span>
      </div>
    </ModalInterior>
  );

  const uploadCenter = (
    <ModalInterior
      title={UPLOAD_ACTIVITY}
      description={<FormattedMessage defaultMessage="Step {currentStep} of {finalStep}" values={{ currentStep: 1, finalStep: 2 }} />}
      closeModal={handleCloseModal}
      goBack={setGoBack()}
      isMounted={linkedState.key === mountedModal}
      onTransitionEnd={handleOnTransitionEnd}
      baseCtas={baseCtas()}
    >
      <div className={styles.dropContainer}>
        {activeFile && (
          <>
            {errorMessage && <div className={formStyles.serverError}>{errorMessage}</div>}
            <TrackFileUpload
              file={activeFile}
              removeFile={removeFile}
              showSmoothOption={showSmoothOption}
              smoothFile={smoothFile}
              handleCheckboxChange={() => toggleSmoothFile(prevState => !prevState)}
              progress={progress}
              uploaded={map && map.id}
              uploading={uploading}
            />
          </>
        )}
      </div>
    </ModalInterior>
  );

  const activityForm = (
    <ModalInterior
      title={editMode ? EDIT_ACTIVIY : UPLOAD_ACTIVITY}
      description={!editMode && <FormattedMessage defaultMessage="Step {currentStep} of {finalStep}" values={{ currentStep: 2, finalStep: 2 }} />}
      closeModal={handleCloseModal}
      goBack={setGoBack()}
      isMounted={linkedState.key === mountedModal}
      onTransitionEnd={handleOnTransitionEnd}
      baseCtas={baseCtas()}
    >
      <TrackUploadFields
        {...trackFormParams}
        updateTrackForm={updateTrackForm}
        handlePrivacyTriggerClick={handlePrivacyTriggerClick}
        className={styles.trackUploadFields}
        trailId={trackFormParams.trailId}
        editMode={editMode}
      />
    </ModalInterior>
  );

  const privacyPolicy = (
    <IndividualPrivacyModal
      {...policyToRadioForm('activity', trackFormParams.contentPrivacy, intl)}
      goBack={setGoBack()}
      closeModal={handleCloseModal}
      isMounted={linkedState.key === mountedModal}
      onTransitionEnd={handleOnTransitionEnd}
      acceptCallback={contentPrivacy =>
        updateTrackFormParams(prevState => {
          return { ...prevState, contentPrivacy };
        })
      }
    />
  );

  const success = (
    <ModalInterior
      goBack={setGoBack()}
      closeModal={handleCloseModal}
      isMounted={linkedState.key === mountedModal}
      onTransitionEnd={handleOnTransitionEnd}
      baseCtas={baseCtas()}
    >
      <div className={styles.trailFormSuccessContainer}>
        <TrailFormSuccess variant={routeMode ? 'route' : 'track'} />
      </div>
    </ModalInterior>
  );

  const modals = {
    dropzonePad,
    uploadCenter,
    activityForm,
    privacyPolicy,
    success
  };

  return (
    <div id="uploadForm">
      <ModalBackground closeModal={handleCloseModal} goBack={setGoBack()}>
        {modals[linkedState.key]}
      </ModalBackground>
    </div>
  );
};

ActivityUploadModal.defaultProps = {
  url: '/api/alltrails/maps/gpx',
  showSmoothOption: true,
  trailId: null,
  onTrackAdd: () => {},
  exploreFlag: false,
  routeMode: false,
  handleRouteUpload: () => {},
  createAsMap: false,
  systemOwned: false,
  trackParams: null,
  disableDropzone: false,
  initialMap: null
};

export default ActivityUploadModal;
